import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Observable as WindowedObservable } from 'windowed-observable';
import { BasketItem, BasketModel } from '../../models';
import { BasketDataTransformer } from '../basket-data';
export class Basket {
    constructor(basketSettings, storageService, basketDataService, basketDataTransformerService, esgTransformerService, shadowBasketStorageService) {
        this.basketSettings = basketSettings;
        this.storageService = storageService;
        this.basketDataService = basketDataService;
        this.basketDataTransformerService = basketDataTransformerService;
        this.esgTransformerService = esgTransformerService;
        this.shadowBasketStorageService = shadowBasketStorageService;
        this.maxNumberOfProducts = this.basketSettings.getSetting().maxNumberOfProducts;
        this.fundAddedOrRemoved$ = new WindowedObservable('fundAddedOrRemoved$');
        this.fundAdded$ = new WindowedObservable('fundAdded$');
        this.fundRemoved$ = new WindowedObservable('fundRemoved$');
        this.basketNotification$ = new WindowedObservable('basketNotification$');
        this.isLoading$ = new WindowedObservable('isLoading$');
        this.freezeEsgUntilSuccess = false;
        this.basketUpdated = new Subject();
        this.removeStuckLoadingFunds('loading...');
        this.esgTransformerService.onAlternativeChange((alternativeData) => this.addAlternativesToBasket(alternativeData));
        this.basketDataTransformerService.onErrorHandler(() => {
            this.removeStuckLoadingFunds('');
        });
    }
    get basketUpdated$() {
        return this.basketUpdated.asObservable();
    }
    onIsLoading(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.isLoading$.subscribe(callBack);
    }
    onIsLoadingUnSubscribe(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.isLoading$.unsubscribe(callBack);
    }
    onBasketChange(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.fundAddedOrRemoved$.subscribe(callBack);
    }
    basketChangeUnSubscribe(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        this.fundAddedOrRemoved$.unsubscribe(callBack);
    }
    onFundAdded(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.fundAdded$.subscribe(callBack);
    }
    fundAddedUnSubscribe(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.fundAdded$.unsubscribe(callBack);
    }
    onFundRemoved(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.fundRemoved$.subscribe(callBack);
    }
    fundRemovedUnSubscribe(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.fundRemoved$.unsubscribe(callBack);
    }
    onBasketNotificationChange(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        return this.basketNotification$.subscribe(callBack);
    }
    basketNotificationUnsubscribe(callBack) {
        BasketDataTransformer.handleCallbackError(callBack);
        this.basketNotification$.unsubscribe(callBack);
    }
    basketNotificationPublishValue(notificationType) {
        if (notificationType) {
            this.basketNotification$.publish(notificationType);
        }
    }
    addAlternativesToBasket(alternativeData) {
        const basket = this.getBasket();
        if (Basket.hasOneNonSustainable(basket === null || basket === void 0 ? void 0 : basket.funds) && (alternativeData === null || alternativeData === void 0 ? void 0 : alternativeData.alternatives)) {
            this.updateAlternatives(alternativeData, basket);
        }
        this.freezeEsgUntilSuccess = false;
    }
    handleAlternatives(basket = this.getBasket(), basketUpdateNeeded = false) {
        const funds = basket === null || basket === void 0 ? void 0 : basket.funds;
        const firstFund = funds && funds[0];
        if ((firstFund === null || firstFund === void 0 ? void 0 : firstFund.alternativeCounter) > 0
            && Basket.getAllAlternatives(funds, firstFund.id).length === (firstFund === null || firstFund === void 0 ? void 0 : firstFund.alternativeCounter)
            && Basket.getAllNonAlternatives(funds).length <= 1) {
            this.esgTransformerService.emitAlternatives(Basket.getAlternativesByFundId(funds, firstFund.id), firstFund.id);
        }
        else if (Basket.hasOneNonSustainable(funds) && !this.freezeEsgUntilSuccess) {
            this.esgTransformerService.transformEsgData(firstFund.id);
            this.freezeEsgUntilSuccess = true;
        }
        else {
            this.emitEmptyAlternatives(funds, basketUpdateNeeded);
        }
    }
    static hasOneNonSustainable(funds) {
        return (funds === null || funds === void 0 ? void 0 : funds.filter((fund) => !fund.isAlternative && !fund.inactivated).length) === 1;
    }
    static getAlternativesByFundId(funds, id) {
        return funds === null || funds === void 0 ? void 0 : funds.filter((fund) => id === fund.nonSustainableId);
    }
    static getAllAlternatives(funds, id) {
        return funds === null || funds === void 0 ? void 0 : funds.filter((fund) => id === fund.nonSustainableId && fund.isAlternative);
    }
    static getAllNonAlternatives(funds) {
        return funds === null || funds === void 0 ? void 0 : funds.filter((fund) => !fund.isAlternative);
    }
    emitEmptyAlternatives(funds, basketUpdateNeeded) {
        if (basketUpdateNeeded) {
            funds = this.getBasketWithActivatedFunds({ funds });
            this.updateBasket({ funds });
        }
        this.esgTransformerService.emitEmptyAlternatives();
    }
    removeStuckLoadingFunds(stuckString) {
        const basket = this.getBasket();
        if (basket && basket.funds && !this.isBasketEmpty()) {
            basket.funds = basket.funds.filter((fund) => fund.name !== stuckString);
            this.storageService.storeBasket(basket);
        }
    }
    /**
     * @return current basket by mapping the basket to its basket items.
     */
    getBasket() {
        return this.storageService.readBasket();
    }
    getBasketWithoutAlternatives() {
        const basketContent = this.storageService.readBasket();
        basketContent.funds = this.hideAlternativesOnToggleState(basketContent.funds);
        return basketContent;
    }
    getBasketWithActivatedFunds(basket = this.getBasketWithoutAlternatives()) {
        const { funds } = basket;
        return funds.filter((fund) => !fund.inactivated);
    }
    getBasketItemById(id, basket = this.getBasket()) {
        return basket.funds.find((f) => id === f.id);
    }
    isBasketEmpty() {
        return this.getBasketCount() === 0;
    }
    /**
     * @return boolean whether the basket is full (based on basketSettings maxNumberOfProducts)
     */
    isBasketFull() {
        return this.getBasketCount() === this.maxNumberOfProducts;
    }
    isBasketFullWithActivatedOnly() {
        return this.getBasketCountWithActivatedFunds() === this.maxNumberOfProducts;
    }
    /**
     * @return number is based on basketSettings maxNumberOfProducts which is max capacity of basket
     */
    getBasketCapacity() {
        return this.maxNumberOfProducts;
    }
    /**
     * @return number of items in basket
     */
    getBasketCount() {
        const { funds } = this.getBasketWithoutAlternatives();
        return funds.length;
    }
    getBasketCountWithActivatedFunds() {
        return this.getBasketWithActivatedFunds().length;
    }
    /**
     * @description updates basket by replacing current basket key
     * @param basket
     * @param loading
     */
    updateBasket(basket, loading = false) {
        var _a;
        this.storageService.storeBasket(basket);
        if (!loading) {
            (_a = this.shadowBasketStorageService) === null || _a === void 0 ? void 0 : _a.storeBasketGateway(this.getBasketIds());
        }
        this.basketUpdated.next(basket);
    }
    updateAlternatives(alternativeData, basket) {
        basket.funds[0].alternativeCounter = alternativeData.alternatives.length;
        alternativeData.alternatives.forEach((alternativeFund) => {
            if (!this.containsFundInAll(alternativeFund.id, basket)) {
                basket.funds.push(new BasketItem(alternativeFund.id, alternativeFund.ticker, alternativeFund.name, alternativeFund.url, alternativeFund.active, alternativeFund.unlinked, alternativeFund.shareClass, alternativeFund.sfdr, alternativeFund.isAlternative, alternativeFund.nonSustainableId, alternativeFund.inactivated, alternativeFund.assetClass, alternativeFund.alternativeCounter, alternativeFund.alternativeOrder));
            }
        });
        this.updateBasket(basket);
    }
    /**
     * @description updates campaign by replacing current campaign key
     * @param key
     */
    updateCampaignId(key) {
        this.storageService.storeCampaignId(key);
    }
    /**
     * @description returns the current campaign id.
     */
    getCampaignId() {
        return this.storageService.readCampaignId();
    }
    /**
     * @deprecated use addFundToBasket instead
     * @description Adds fund to the basket as long as it does not exceed the max number allowed
     * @param fund of type BasketItem
     */
    addToBasket(fund) {
        if (this.isBasketFull()) {
            this.basketNotification$.publish('error');
        }
        this.addFundToBasket(fund.id);
    }
    addFundToBasket(portfolioId, emptyAlternatives = true) {
        if (this.isBasketFull()) {
            this.basketNotification$.publish('error');
        }
        if (this.isFundAlternative(portfolioId)) {
            this.activateAlternativeFund(portfolioId, emptyAlternatives);
        }
        else {
            this.addFundsToBasket([portfolioId], emptyAlternatives);
        }
    }
    addFundsToBasket(portfolioIds, emptyAlternatives = false) {
        const basket = this.getBasket();
        if (emptyAlternatives) {
            this.emitEmptyAlternatives(basket.funds, true);
        }
        const addablePortfolioIds = this.getAddableFunds(portfolioIds);
        if (basket && basket.funds) {
            addablePortfolioIds.forEach((portfolioId) => {
                this.isLoading$.publish(true);
                basket.funds.push(new BasketItem(portfolioId, '', '', '', false, true));
            });
            // fill basket with loading state items until data is returned from Backend
            this.updateBasket(basket, true);
        }
        this.getProductData(addablePortfolioIds).subscribe((funds) => this.storeFundsInBasket(addablePortfolioIds, funds));
    }
    storeFundsInBasket(addablePortfolioIds, funds, basket = this.getBasket()) {
        funds
            .filter((fund) => fund)
            .forEach((fund) => {
            fund.timestamp = Date.now();
            const index = basket.funds.findIndex((basketItem) => basketItem.id === fund.id);
            basket.funds[index] = fund;
        });
        this.updateBasket(basket);
        this.isRemoved = false;
        this.publishBasketChange(basket.funds, this.isRemoved);
        this.isLoading$.publish(false);
        this.fundAddedPublish(addablePortfolioIds);
        this.handleAlternatives(basket, true);
    }
    isFundAddable(fundId, basket = this.getBasket()) {
        return !this.containsFund(fundId, basket) && this.getBasketCount() < this.maxNumberOfProducts;
    }
    fundAddedPublish(portfolioIds) {
        let fund;
        portfolioIds.forEach((addablePortfolioId) => {
            fund = this.getBasketItemById(addablePortfolioId);
        });
        if (fund === null || fund === void 0 ? void 0 : fund.id) {
            this.fundAdded$.publish(fund);
        }
    }
    getAddableFunds(fundIds) {
        const emptyPlacesInBasket = this.maxNumberOfProducts - this.getBasketCount();
        return fundIds.filter((id) => this.isFundAddable(id)).slice(0, emptyPlacesInBasket);
    }
    addToBasketOrdered(id, index) {
        const basket = this.getBasket();
        if (this.isFundAddable(id)) {
            this.isLoading$.publish(true);
            this.getProductData([id]).subscribe((funds) => {
                basket.funds.splice(index, 0, funds[0]);
                this.updateBasket(basket);
                this.isLoading$.publish(false);
            });
        }
    }
    getProductData(portfolioIds) {
        return this.basketDataService
            .getProductData(portfolioIds)
            .pipe(map((response) => this.basketDataTransformerService.mapApiResponseToBasketItems(response.data, portfolioIds)));
    }
    static moveItemInBasket(id, funds) {
        let moveFromIndex = 0;
        let moveToIndex = 0;
        for (let i = funds.length - 1; i >= 0; i -= 1) {
            // necessary to have the first inactivated
            const fund = funds[i];
            if (fund.id === id) {
                moveFromIndex = i;
            }
            if (fund.inactivated) {
                moveToIndex = i;
            }
        }
        if (moveToIndex !== moveFromIndex) {
            funds.splice(moveToIndex, 0, funds.splice(moveFromIndex, 1)[0]);
        }
        return funds;
    }
    moveAlternativesToTheRightOrder(basket) {
        const { funds } = basket;
        const inactivatedFunds = funds.filter((fund) => fund.inactivated);
        const activeFunds = this.getBasketWithActivatedFunds(basket);
        inactivatedFunds.sort((a, b) => a.alternativeOrder - b.alternativeOrder);
        return [...activeFunds, ...inactivatedFunds];
    }
    /**
     * @description If fund exists in the basket, then remove it
     * @param fundId of type number
     */
    removeFromBasket(fundId) {
        const basket = this.getBasket();
        if (this.containsFund(fundId, basket)) {
            const fundToBeRemoved = this.getBasketItemById(fundId);
            if (!(fundToBeRemoved === null || fundToBeRemoved === void 0 ? void 0 : fundToBeRemoved.nonSustainableId)) {
                basket.funds = this.handleNonSustainableRemove(basket.funds, fundToBeRemoved);
            }
            else {
                basket.funds = Basket.handleSustainableRemove(basket.funds, fundToBeRemoved);
            }
        }
        else {
            basket.funds = this.removeGhosts(basket.funds, fundId);
        }
        this.isRemoved = true;
        basket.funds = this.moveAlternativesToTheRightOrder(basket);
        this.updateBasket(basket);
        this.fundRemoved$.publish(fundId);
        this.publishBasketChange(basket.funds, this.isRemoved);
        this.isLoading$.publish(false);
        this.handleAlternatives(basket);
        this.storageService.storeAlternativeToggleState(null);
    }
    getRemovableAlternatives(removedFundId) {
        const { funds } = this.getBasket();
        return funds.filter((fund) => fund.nonSustainableId === removedFundId && !!fund.inactivated);
    }
    /*
     * Ghost can be only removed here since inactivated is true
     * TODO: check later if needed since ghost are not going to have bin/remove icons
     */
    removeGhosts(funds, id) {
        return this.switchAlternativesToActivated(funds).filter((f) => f.id !== id);
    }
    isFundAlternative(id) {
        var _a;
        return (_a = this.getBasketItemById(id)) === null || _a === void 0 ? void 0 : _a.isAlternative;
    }
    handleNonSustainableRemove(funds, fund) {
        if (fund.alternativeCounter) {
            funds = Basket.removeAlternatives(funds, fund.id);
            funds = this.resetAlternatives(funds);
        }
        funds = funds.filter((f) => f.id !== fund.id);
        return funds;
    }
    static handleSustainableRemove(funds, fund) {
        return funds.map((f) => {
            if (!f.inactivated && f.isAlternative && fund.id === f.id) {
                f.inactivated = true;
            }
            return f;
        });
    }
    static removeAlternatives(funds, removedFundId) {
        return funds.filter((fund) => fund.nonSustainableId !== removedFundId
            || (fund.nonSustainableId === removedFundId && !fund.inactivated));
    }
    resetAlternatives(funds) {
        if (!this.isBasketEmpty()) {
            return funds.map((fund) => {
                if (fund.isAlternative) {
                    fund.isAlternative = false;
                    fund.nonSustainableId = null;
                }
                return fund;
            });
        }
        return funds;
    }
    /**
     * @description Remove all funds from the basket
     */
    removeAllFromBasket() {
        this.updateBasket(new BasketModel());
        this.isRemoved = true;
        this.publishBasketChange();
        this.handleAlternatives();
        this.storageService.storeAlternativeToggleState(null);
    }
    /**
     * @description Returns whether or not a specific fund exists in the basket
     * @param fundId of type number
     * @param basket the basket to check
     * @return whether the given basket contains the fund or not
     */
    containsFund(fundId, basket = this.getBasket()) {
        return !!basket && basket.funds.some((fund) => fund.id === fundId && !fund.inactivated);
    }
    hasInactivatedAlternatives(basket = this.getBasket()) {
        return basket === null || basket === void 0 ? void 0 : basket.funds.some((fund) => fund.inactivated && fund.isAlternative);
    }
    containsFundInAll(fundId, basket = this.getBasket()) {
        return basket.funds.some((fund) => fund.id === fundId);
    }
    activateAlternativeFund(id, emptyAlternatives = false) {
        let { funds } = this.getBasket();
        if (!emptyAlternatives) {
            funds = Basket.moveItemInBasket(id, funds);
        }
        funds = funds.map((fund) => {
            if (fund.id === id) {
                fund.inactivated = false;
            }
            return fund;
        });
        this.updateBasket({ funds });
        if (emptyAlternatives) {
            this.resetAlternatives(funds);
            this.emitEmptyAlternatives(funds, true);
        }
        this.fundAddedPublish([id]);
        this.publishBasketChange(funds, false);
    }
    /**
     * @description Returns basket funds as a ticker string
     * @return string
     */
    getBasketFundsAsString() {
        const basket = this.getBasketWithoutAlternatives();
        const { funds } = basket;
        let str = '';
        funds.forEach((fund) => {
            str += `-${fund.ticker}`;
        });
        return str;
    }
    /**
     * @description Returns basket funds' ticker in a format what analytics needs
     * @return string
     */
    getBasketFundTicker() {
        const basket = this.getBasketWithoutAlternatives();
        const { funds } = basket;
        let tickersToString = '';
        for (let i = 0; i < funds.length; i += 1) {
            if (i === 0) {
                tickersToString += Basket.getTickerString(funds, i);
            }
            else {
                tickersToString += `-${Basket.getTickerString(funds, i)}`;
            }
        }
        return tickersToString;
    }
    static getTickerString(funds, index) {
        return funds[index].ticker === '' ? '-' : funds[index].ticker;
    }
    getBasketIds() {
        let { funds } = this.getBasket();
        funds = this.hideAlternativesOnToggleState(funds);
        return funds.map((fund) => fund.id);
    }
    getActivatedBasketIds() {
        let { funds } = this.getBasket();
        funds = this.getBasketWithActivatedFunds({ funds });
        return funds.map((fund) => fund.id);
    }
    hideAlternativesOnToggleState(funds) {
        var _a;
        if (!((_a = this.storageService.readAlternativeStates()) === null || _a === void 0 ? void 0 : _a.toggleState)) {
            funds = this.getBasketWithActivatedFunds({ funds });
        }
        return funds;
    }
    toggleActivationForAlternatives(isActivated) {
        let { funds } = this.getBasket();
        funds = funds.map((fund) => {
            if (!isActivated && fund.isAlternative) {
                fund.inactivated = true;
            }
            return fund;
        });
        this.updateBasket({ funds });
    }
    activateAllAlternativeFund() {
        const { funds } = this.getBasket();
        this.updateBasket({ funds: this.switchAlternativesToActivated(funds) });
    }
    switchAlternativesToActivated(funds) {
        const basketCapacity = this.getBasketCapacity();
        return funds.map((fund, i) => {
            if (fund.isAlternative && i < basketCapacity) {
                fund.inactivated = false;
            }
            return fund;
        });
    }
    publishBasketChange(funds = [], isRemoved = true) {
        this.fundAddedOrRemoved$.publish({
            funds: this.getBasketWithActivatedFunds(new BasketModel(funds)),
            isRemoved,
        });
    }
    /**
     * @description Returns basket funds as ID string
     * @returns string
     */
    getBasketFundsAsIdString() {
        const basket = this.getBasketWithoutAlternatives();
        const { funds } = basket;
        let str = '';
        funds.forEach((fund) => {
            if (str !== '') {
                str += '-';
            }
            str += fund.id;
        });
        if (str === '') {
            str = 'empty';
        }
        return str;
    }
    /**
     * @description Returns basket funds' ticker in a string array ['AGG', 'IVV']
     *
     */
    getBasketFundsTickerArray() {
        const basket = this.getBasket();
        const { funds } = basket;
        const fundsArray = [];
        funds.forEach((fund) => {
            if (fund.ticker !== '') {
                fundsArray.push(fund.ticker);
            }
        });
        return fundsArray;
    }
    /**
     * @description Returns basket funds' ticker and id as string for analytics
     *
     */
    getConcatenatedTicketAndId() {
        return `|${this.getBasketFundTicker()}|${this.getBasketFundsAsIdString()}`;
    }
    /**
     * @description Make a specific fund the primary fund if it is in the basket
     * @param fund of type BasketItem
     */
    makePrimary(fund) {
        const basket = this.getBasket();
        const isNotFirstElement = (index) => [-1, 0].indexOf(index) === -1;
        const indexOfFund = basket.funds.findIndex((item) => item.id === fund.id);
        if (isNotFirstElement(indexOfFund)) {
            const temp = basket.funds[0];
            basket.funds[0] = fund;
            basket.funds[indexOfFund] = temp;
            this.updateBasket(basket);
        }
    }
    handleAlternativesWrapper() {
        this.handleAlternatives();
    }
}
